import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateServicesAction, serviceOneSelector, updateServiceParentAction, copyServicesByParentIdAction, loadingStatusSelector } from '../../../reducers/servicesReducer';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Modal, Box } from '@mui/material';
import { KTSVG } from '../../../../_metronic/helpers';
import { array_to_CSV } from '../../../../util';
import IconsLibrary from '../components/IconsLibrary';
import { CgPathDivide } from 'react-icons/cg';
import { MuiTooltip } from '../../../common/Tooltip';
import { FiEdit2 } from 'react-icons/fi';
import InfoWrapper from '../../../modules/common/SectionInfo';
import { iconListSelector } from '../../../reducers/iconReducer';
import React from 'react';

interface SubmissionData {
  id?: number;
  name: string;
  icon: string;
  isActive: number;
  isMemberDefault: number;
  isAdmin: number;
  priority?: number | null;
  level?: number;
  parentId?: number;
  serviceIdentifier?: {
  name: string;
  };
}


interface UpdateModalProps {
  open: boolean;
  onClose: () => void;
  data: SubmissionData;
  onSubmit: (data: SubmissionData) => void;
  isCopy: boolean;
  level: number;
  selectedRow: any;
}

export const useServiceTableConfig = (level: number) => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const loading = useSelector(loadingStatusSelector);
  const [isCopy, setIsCopy] = useState(false);
  const [isTransformModalOpen, setIsTransformModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [activeLoadingRowId, setActiveLoadingRowId] = useState<number | null>(null);
  const [memberDefaultLoadingRowId, setMemberDefaultLoadingRowId] = useState<number | null>(null);
  const [copyData, setCopyData] = useState<any>(null);
  const [error, setError] = useState(false);
  const channels = useSelector(serviceOneSelector);
  const selectRef = useRef<HTMLSelectElement>(null);
  const selectCopyRef = useRef<HTMLSelectElement>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [submissionData, setSubmissionData] = useState<any>({});

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'white',
    boxShadow: 24,
    p: 4,
    height: 700,
    overflow: 'scroll',
  };

  const iconModalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'white',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    height: 600,
    overflow: 'scroll',
  };

  const handleOpenTransformModal = (row: any) => {
    setSelectedRow(row);

    setIsTransformModalOpen(true);
  };

  const handleCloseTransformModal = () => {
    setSelectedRow(null);
    setIsTransformModalOpen(false);
    setError(false);
    if (selectRef.current) {
      selectRef.current.value = '';
    }
  };

  const handleOpenCopyModal = (row: any) => {
    setSelectedRow(row);
    setIsCopyModalOpen(true);
  };

  const handleCloseCopyModal = () => {
    setSelectedRow(null);
    setIsCopyModalOpen(false);
    setCopyData(null);
  };


  const handleParentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const newParentId = selectRef.current?.value;
    if (newParentId) {
      dispatch(
        updateServiceParentAction({
          data: {
            serviceId: selectedRow?.id,
            newParentId: Number(newParentId)
          },
          serviceId: selectedRow?.id
        })
      );
      handleCloseTransformModal();
    } else {
      setError(true);
    }
  };

  const handleCopySubmit = () => {
    const newParentId = selectCopyRef.current?.value;
    if (newParentId) {
      const finalData = {
        parentId: newParentId,
        serviceIds: array_to_CSV([selectedRow?.id]),
      };
      dispatch(copyServicesByParentIdAction({ data: finalData, setCopyData }));
      handleCloseCopyModal();
    } else {
      setError(true);
    }
  };

  const handleOpenUpdateModal = (row: any) => {
    setSelectedRow(row);
    // Check if there's a saved priority value from previous error
    const savedPriority = row?.id ? localStorage.getItem(`priority_${row.id}`) : null;
    const priorityValue = savedPriority !== null 
      ? (savedPriority === '' ? null : Number(savedPriority))
      : (typeof row?.priority === 'number'
          ? row?.priority
          : row?.priority
          ? Number(row?.priority)
          : null);
    
    setSubmissionData({
      ...row,
      name: row?.name || '',
      icon: row?.icon || '',
      isActive: row?.isActive || 0,
      isMemberDefault: row?.isMemberDefault || 0,
      isAdmin: row?.isAdmin || 0,
      priority: priorityValue
    });
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    // Clear localStorage when modal closes
    if (selectedRow?.id) {
      localStorage.removeItem(`priority_${selectedRow.id}`);
    }
    setSelectedRow(null);
    setSubmissionData({});
    setIsUpdateModalOpen(false);
  };

  const handleSubmit = async (data: SubmissionData) => {
    if (selectedRow) {
      const result = await dispatch(updateServicesAction({ data, id: selectedRow.id, level }));
      if (updateServicesAction.fulfilled.match(result) && result.payload?.success) {
        // Clear localStorage on successful submit
        if (selectedRow?.id) {
          localStorage.removeItem(`priority_${selectedRow.id}`);
        }
        handleCloseUpdateModal();
      }
    }
  };

  const columns = [
    {
      id: 'index',
      label: '#',
      align: 'left',
      render: (_: any, index: number, currentPage: number, entriesPerPage: number) => (
        <span>{(currentPage - 1) * entriesPerPage + index + 1}</span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      render: (row: any) => (
        <div className='d-flex align-items-center' style={{ maxWidth: '130px', whiteSpace: 'normal' }}>
          <img
            src={row?.icon}
            alt='icon'
            style={{
              width: 30,
              height: 30,
              marginRight: '10px',
              filter: row?.isActive === 1 ? 'none' : 'grayscale(100%)'
            }}
          />
          <span>{row?.name || '—'}</span>
          {row?.level === 2 && (
            <MuiTooltip title='Edit Name'>
              <div className='ms-4' onClick={() => handleOpenUpdateModal(row)}>
                <FiEdit2 className='pointer' size={'0.8rem'} />
              </div>
            </MuiTooltip>
          )}
          {row?.level === 3 && (
            <MuiTooltip title='Edit Name'>
              <div className='ms-4' onClick={() => navigate(`/channels/update/${row.id}`)}>
                <FiEdit2 className='pointer' size={'0.8rem'} />
              </div>
            </MuiTooltip>
          )}
        </div>
      ),
    },

    {
      id: 'icon',
      label: 'Icon',
      align: 'left',
      render: (row: any) => (
        <div className='d-flex align-items-center' style={{ maxWidth: '200px' }}>
          <div className='text-truncate' style={{ flex: 1 }}>{row?.icon || '—'}</div>
          {(row.level === 1 || row.level === 2) && (
            <MuiTooltip title='Edit Icon'>
              <div className='ms-3' onClick={() => handleOpenUpdateModal(row)}>
                <FiEdit2 className='pointer' size={'0.8rem'} />
              </div>
            </MuiTooltip>
          )}
          {(row.level === 3) && (
            <MuiTooltip title='Edit Icon'>
              <div className='ms-3' onClick={() => navigate(`/channels/update/${row.id}`)}>
                <FiEdit2 className='pointer' size={'0.8rem'} />
              </div>
            </MuiTooltip>
          )}


        </div>
      )
    },
    ...(level === 3 ? [
      {
        id: 'description',
        label: 'Description',
        align: 'left',
        render: (row: any) => (
          <div style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
            {row?.description || '—'}
          </div>
        ),
      },
      {
        id: 'duration',
        label: 'Duration',
        align: 'left',
        render: (row: any) => (
          <div>
            {row?.duration ? `${row.duration} min` : '—'}
          </div>
        ),
      },
      {
        id: 'price',
        label: 'Price',
        align: 'left',
        render: (row: any) => (
          <div>
            {row?.price ? `$${row.price}` : '—'}
          </div>
        ),
      }
    ] : []),
    {
      id: 'isAdmin',
      label: 'isAdmin',
      align: 'left',
      render: (row: any) => (
        <div className='form-check form-check form-switch fv-row'>
          <input
            checked={row?.isAdmin === 1}
            onChange={(e) => {
              if (row?.isAdmin === 1) return;
              const finalData = {
                ...row,
                isAdmin: e.target.checked ? 1 : 0,
              };
              dispatch(updateServicesAction({ data: finalData, id: row.id, level }));
            }}
            disabled={row?.isAdmin === 1}
            className={`form-check-input w-40px h-25px`}
            type='checkbox'
            id='isAdmin'
          />
          <label className='form-check-label'></label>
        </div>
      ),
    },
    {
      id: 'isActive',
      label: 'isActive',
      align: 'left',
      render: (row: any) => (
        <div className='form-check form-check form-switch fv-row'>
          <input
            checked={row?.isActive === 1}
            onChange={(e) => {
              setActiveLoadingRowId(row.id);
              const finalData = {
                ...row,
                isActive: e.target.checked ? 1 : 0,
                isMemberDefault: e.target.checked === false ? 0 : row?.isMemberDefault,
              };
              dispatch(updateServicesAction({ data: finalData, id: row.id, level }));
            }}
            className={`form-check-input w-40px h-25px `}
            type='checkbox'
            disabled={loading === 'loading' && row.id === activeLoadingRowId}
            id='isActive'
          />
          <label className='form-check-label'></label>
        </div>
      ),
    },
    ...(level !== 3 ? [{
      id: 'isMemberDefault',
      label: 'isMemberDefault',
      align: 'left',
      render: (row: any) => (
        <div className='form-check form-check form-switch fv-row'>
          <input
            checked={row?.isMemberDefault === 1}
            onChange={(e) => {
              if (row?.isAdmin === 2 || row?.isAdmin === 3) {
                return;
              } else {
                setMemberDefaultLoadingRowId(row.id);
                const finalData = {
                  ...row,
                  isMemberDefault: e.target.checked ? 1 : 0,
                };
                dispatch(updateServicesAction({ data: finalData, id: row.id, level }));
              }
            }}
            className={`form-check-input w-40px h-25px`}
            disabled={
              row?.isAdmin === 2 ||
              row?.isAdmin === 3 ||
              row?.isActive !== 1 ||
              (loading === 'loading' && row.id === memberDefaultLoadingRowId)
            }
            type='checkbox'
            id='isMemberDefault'
          />
          <label className='form-check-label'></label>
        </div>
      ),
    }] : []),

    ...(level !== 3 ? [{
      id: 'priority',
      label: 'Priority',
      align: 'left',
      render: (row: any) => (
        <div className='d-flex align-items-center' style={{ maxWidth: '150px' }}>
          <div style={{ flex: 1 }}>{(row?.priority ?? row?.priority === 0) ? row?.priority : '—'}</div>
            <MuiTooltip title='Edit Priority'>
              <div className='ms-3' onClick={() => handleOpenUpdateModal(row)}>
                <FiEdit2 className='pointer' size={'0.8rem'} />
              </div>
            </MuiTooltip>
        </div>
      )
    }] : []),
    {
      id: 'actions',
      label: 'Actions',
      align: 'center',
      render: (row: any) => (
        <div className='d-flex justify-content-center align-items-center gap-2'>
          <div
            className={`d-flex align-items-center ${row.level !== 3 ? 'btn btn-sm ' + (row?.isActive === 1 ? 'btn-primary' : 'btn-secondary') : ''}`}
            style={row.level !== 3 ? { padding: '0.5rem 1rem', fontSize: '0.875rem' } : {}}
            onClick={() =>
              navigate(
                row.level == 1
                  ? `/channels/lv1/${row?.id}`
                  : row.level == 2 ? `/channels/lv1/${row?.parentId}/lv2/${row?.id}`
                    : `/channels/update/${row.id}`,
                {
                  state: row?.name,
                }
              )
            }
          >
            {row.level == 1 ? 'Sub Channels' : row.level == 2 ? 'Services' : (
              <img src="/media/icons/duotune/general/gen055.svg" alt="update" style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
            )}
          </div>
          {row?.level == 2 && (
            <>
              <button
                onClick={() => handleOpenTransformModal(row)}
                type='button'
                className='btn btn-sm btn-primary'
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              >
                Transform
              </button>
              <button
                onClick={() => handleOpenCopyModal(row)}
                type='button'
                className='btn btn-sm btn-primary'
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              >
                Copy sub channel
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const getTableData = (data: any[], currentPage: number, entriesPerPage: number) => {
    if (!data || !Array.isArray(data)) {
      return {
        table: { columns: [], rows: [] },
        pagination: { total: 0, lastPage: 0, currentPage: 1, entriesPerPage: 10 }
      };
    }

    // Ensure currentPage and entriesPerPage are valid numbers
    const validCurrentPage = Math.max(1, Math.floor(currentPage || 1));
    const validEntriesPerPage = Math.max(1, Math.floor(entriesPerPage || 10));

    // Calculate pagination values
    const total = data.length;
    const lastPage = Math.max(1, Math.ceil(total / validEntriesPerPage));
    const startIndex = (validCurrentPage - 1) * validEntriesPerPage;
    const endIndex = Math.min(startIndex + validEntriesPerPage, total);

    // Get the slice of data for the current page
    const slicedData = data.slice(startIndex, endIndex);

    const rows = slicedData.map((row: any, index: number) => {
      const newRow: any = {};
      columns.forEach((col) => {
        if (col.render) {
          newRow[col.id] = col.render(row, index, validCurrentPage, validEntriesPerPage);
        } else {
          newRow[col.id] = row[col.id];
        }
      });
      return newRow;
    });

    return {
      table: {
        columns,
        rows
      },
      pagination: {
        total,
        lastPage,
        currentPage: validCurrentPage,
        entriesPerPage: validEntriesPerPage
      }
    };
  };

  const UpdateModal: React.FC<UpdateModalProps> = ({
    open,
    onClose,
    data,
    onSubmit,
    isCopy,
    level,
    selectedRow
  }) => {
    const storageKey = selectedRow?.id ? `priority_${selectedRow.id}` : null;
    const [formData, setFormData] = useState<SubmissionData>(data);
    const icons = useSelector(iconListSelector);
    const [libraryIcon, setLibraryIcon] = useState('');
    const [isIconModalOpen, setIsIconModalOpen] = useState(false);

    useEffect(() => {
      // When data changes (including on re-render after API error), restore priority from localStorage
      if (storageKey) {
        const savedPriority = localStorage.getItem(storageKey);
        if (savedPriority !== null) {
          try {
            const priorityValue = savedPriority === '' ? null : Number(savedPriority);
            setFormData({ ...data, priority: priorityValue });
            return;
          } catch (e) {
            // If parsing fails, use data as is
          }
        }
      }
      setFormData(data);
    }, [data, storageKey]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        name: e.target.value,
      }));
    }, []);

    const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        icon: e.target.value,
      }));
    }, []);

    const handleIsActiveChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        isActive: e.target.checked ? 1 : 0,
        isMemberDefault: prev.isActive === 0 ? 0 : prev.isMemberDefault,
      }));
    }, []);

    const handleIsMemberDefaultChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedRow?.isAdmin === 2 || selectedRow?.isAdmin === 3) return;
      setFormData((prev: SubmissionData) => ({
        ...prev,
        isMemberDefault: e.target.checked ? 1 : 0,
      }));
    }, [selectedRow?.isAdmin]);

    const handleIsAdminChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedRow?.isAdmin === 1) return;
      setFormData((prev: SubmissionData) => ({
        ...prev,
        isAdmin: e.target.checked ? 1 : 0,
      }));
    }, [selectedRow?.isAdmin]);

    const handleFormSubmit = useCallback((e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    }, [onSubmit, formData]);

    const handleOpenIconModal = useCallback(() => {
      setIsIconModalOpen(true);
    }, []);

    const handleCloseIconModal = useCallback(() => {
      setIsIconModalOpen(false);
      setLibraryIcon('');
    }, []);

    const handleIconSelect = useCallback((icon: string) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        icon,
      }));
      handleCloseIconModal();
    }, [handleCloseIconModal]);

    const formContent = useMemo(() => (
      <form className='text-center m-5' onSubmit={handleFormSubmit}>
        <InfoWrapper bar={'down'}>
          <div>
            <div className='row mb-4 text-start'>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Name</span>
                </label>
                <div className='fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Service Name'
                    value={formData?.name || ''}
                    disabled={selectedRow?.level === 1}
                    onChange={handleNameChange}
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Icon</span>
                </label>
                <div className='d-flex'>
                  <input
                    type='text'
                    value={formData?.icon || ''}
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Icon Link'
                    onChange={handleIconChange}
                    disabled={true}
                  />
                  <MuiTooltip title='Add Icon from Library'>
                    <div className='col-lg-1 text-center m-auto' onClick={handleOpenIconModal}>
                      <CgPathDivide className='ms-3 pointer' size={'2rem'} />
                    </div>
                  </MuiTooltip>
                </div>
              </div>
            </div>
            <div className='row mb-4 text-start'>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span>Priority</span>
                </label>
                <div className='fv-row'>
                  <input
                    type='number'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    min={1}
                    step={1}
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Priority'
                    value={
                      typeof formData?.priority === 'number' && formData?.priority > 0
                        ? String(formData?.priority)
                        : ''
                    }
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                      if (digitsOnly === '') {
                        const newData = {
                          ...formData,
                          priority: null
                        };
                        setFormData(newData);
                        // Save to localStorage immediately
                        if (storageKey) {
                          localStorage.setItem(storageKey, '');
                        }
                        return;
                      }
                      const parsed = Number(digitsOnly);
                      const newData = {
                        ...formData,
                        priority: parsed >= 1 ? parsed : null
                      };
                      setFormData(newData);
                      // Save to localStorage immediately
                      if (storageKey && parsed >= 1) {
                        localStorage.setItem(storageKey, String(parsed));
                      } else if (storageKey) {
                        localStorage.setItem(storageKey, '');
                      }
                    }}
                    onBlur={(e) => {
                      const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                      const parsed = digitsOnly ? Number(digitsOnly) : NaN;
                      if (!digitsOnly || Number.isNaN(parsed) || parsed < 1) {
                        const newData = {
                          ...formData,
                          priority: null
                        };
                        setFormData(newData);
                        // Save to localStorage
                        if (storageKey) {
                          localStorage.setItem(storageKey, '');
                        }
                      } else {
                        e.currentTarget.value = String(parsed);
                        const newData = {
                          ...formData,
                          priority: parsed
                        };
                        setFormData(newData);
                        // Save to localStorage
                        if (storageKey) {
                          localStorage.setItem(storageKey, String(parsed));
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            {selectedRow?.level === 1 && (
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6 text-start d-block'>
                  <span className=''>Service identifier</span>
                </label>
                <div className='fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid text-start'
                    placeholder='Service type'
                    disabled
                    value={formData?.serviceIdentifier?.name || ''}
                  />
                </div>
              </div>
            )}
          </div>
        </InfoWrapper>
        <div className='row'>
          <div className='mb-4 col-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Member Default</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    checked={formData?.isMemberDefault === 1}
                    onChange={handleIsMemberDefaultChange}
                    className='form-check-input w-40px h-25px'
                    disabled={
                      selectedRow?.isAdmin === 2 ||
                      selectedRow?.isAdmin === 3 ||
                      formData?.isActive !== 1
                    }
                    type='checkbox'
                    id='isMemberDefault'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div>
          <div className='mb-4 col-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Active</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    checked={formData?.isActive === 1}
                    onChange={handleIsActiveChange}
                    className='form-check-input w-40px h-25px'
                    type='checkbox'
                    id='isActive'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div>
          <div className='mb-4 col-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Admin</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    checked={formData?.isAdmin === 1}
                    onChange={handleIsAdminChange}
                    disabled={selectedRow?.isAdmin === 1}
                    className='form-check-input w-40px h-25px'
                    type='checkbox'
                    id='isAdmin'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='d-flex justify-content-center mt-20'>
          <button
            type='submit'
            className='btn btn-primary mt-4'
            aria-label='submit data'
          >
            Submit
          </button>
        </div>
      </form>
    ), [
      formData,
      selectedRow,
      handleNameChange,
      handleIconChange,
      handleIsMemberDefaultChange,
      handleIsActiveChange,
      handleIsAdminChange,
      handleFormSubmit,
      handleOpenIconModal
    ]);

    return (
      <>
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box className='overflow-scroll' sx={style}>
            <div className='modal-header'>
              {selectedRow?.level === 1 ? (
                <h2 className='fw-bolder'>Update Channel</h2>
              ) : (
                <h2 className='fw-bolder'>Update Sub Channel</h2>
              )}
              <div
                onClick={onClose}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            {formContent}
          </Box>
        </Modal>

        <Modal
          open={isIconModalOpen}
          onClose={handleCloseIconModal}
          aria-labelledby="icon-modal-title"
          aria-describedby="icon-modal-description"
        >
          <Box sx={iconModalStyle}>
            <div className='modal-header mb-2'>
              <h2 className='fw-bolder'>Icons</h2>
              <div
                onClick={handleCloseIconModal}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            <div className='card-title text-center'>
              <div className='d-flex align-items-center my-3 m-auto'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-6'
                />
                <input
                  onChange={(e) => setLibraryIcon(e.target.value)}
                  type='text'
                  data-kt-user-table-filter='search'
                  className='form-control form-control-solid w-250px ps-14'
                  placeholder='Search icons'
                />
              </div>
            </div>
            <div className='row mt-4'>
              {libraryIcon
                ? icons
                  ?.filter((f) => f?.name?.toUpperCase().includes(libraryIcon.toUpperCase()))
                  ?.map((icon: any, index: any) => (
                    <div className='col-3 rounded text-center my-1' key={index}>
                      <div
                        style={{ width: '100px', height: '50px' }}
                        className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                        onClick={() => handleIconSelect(icon.icon)}
                        data-bs-toggle='tooltip'
                        title={icon.name}
                      >
                        <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                          <img src={icon.icon} alt='icon' width={30} height={30} />
                        </p>
                      </div>
                      <div className='fs-7'>{icon.name}</div>
                    </div>
                  ))
                : icons?.map((icon: any, index: any) => (
                  <div className='col-3 rounded text-center my-1' key={index}>
                    <div
                      style={{ width: '100px', height: '50px' }}
                      className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                      onClick={() => handleIconSelect(icon.icon)}
                      data-bs-toggle='tooltip'
                      title={icon.name}
                    >
                      <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                        <img src={icon.icon} alt='icon' width={30} height={30} />
                      </p>
                    </div>
                    <div className='fs-7'>{icon.name}</div>
                  </div>
                ))}
            </div>
          </Box>
        </Modal>
      </>
    );
  };

  useEffect(() => {
    if (loading !== 'loading') {
      setActiveLoadingRowId(null);
      setMemberDefaultLoadingRowId(null);
    }
  }, [loading]);

  return {
    columns,
    getTableData,
    isCopy,
    setIsCopy,
    Modals: () => (
      <>
        <Modal
          open={isTransformModalOpen}
          onClose={handleCloseTransformModal}
          aria-labelledby="transform-modal-title"
          aria-describedby="transform-modal-description"
        >
          <Box sx={style}>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Update Channel</h2>
              <div
                onClick={handleCloseTransformModal}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            <form className='text-center m-5' onSubmit={handleParentUpdate}>
              <div className='row mb-4 text-start'>
                <label className='col-sm-3 col-form-label fw-bold fs-6'>
                  <span>Sub Channel</span>
                </label>
                <div className='col-sm-9 fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Service Name'
                    value={selectedRow?.name || ''}
                    disabled={true}
                  />
                </div>
              </div>
              <div className='row mb-4 text-start'>
                <label className='col-sm-3 col-form-label fw-bold fs-6'>
                  <span>Current Channel</span>
                </label>
                <div className='col-sm-9 fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Service Name'
                    value={channels?.find((f: any) => f?.id === selectedRow?.parentId)?.name || ''}
                    disabled={true}
                  />
                </div>
              </div>
              <div className='row mb-4 text-start'>
                <label className='col-sm-3 col-form-label fw-bold fs-6'>
                  <span className='required'>New Channel</span>
                </label>
                <div className='col-sm-9 fv-row'>
                  <select
                    ref={selectRef}
                    className='form-select form-select-solid form-select-lg fw-bold'
                    defaultValue=""
                  >
                    <option value="">Select a Parent...</option>
                    {channels
                      ?.filter((channel: any) => channel?.id !== selectedRow?.parentId)
                      ?.map((channel: any) => (
                        <option key={channel?.id} value={channel?.id}>
                          {channel?.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              {error && <p className='text-danger'>New Channel field is required for continue</p>}
              <button
                type='submit'
                className='btn btn-primary mt-20'
                aria-label='submit data'
              >
                Transform
              </button>
            </form>
          </Box>
        </Modal>

        <Modal
          open={isCopyModalOpen}
          onClose={handleCloseCopyModal}
          aria-labelledby="copy-modal-title"
          aria-describedby="copy-modal-description"
        >
          <Box sx={style}>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Copy Sub Channel</h2>
              <div
                onClick={handleCloseCopyModal}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            <form className='text-center m-5'>
              <div className='row mb-4 text-start'>
                <label className='col-sm-3 col-form-label fw-bold fs-6'>
                  <span>Sub Channel</span>
                </label>
                <div className='col-sm-9 fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Service Name'
                    value={selectedRow?.name}
                    disabled={true}
                  />
                </div>
              </div>
              <div className='row mb-4 text-start'>
                <label className='col-sm-3 col-form-label fw-bold fs-6'>
                  <span className='required'>Target Channel</span>
                </label>
                <div className='col-sm-9 fv-row'>
                  <select
                    className='form-select form-select-solid form-select-lg fw-bold'
                    ref={selectCopyRef}

                  >
                    <option value=''>Select a Channel...</option>
                    {channels?.map((channel: any) => (
                      <option key={channel?.id} value={channel?.id}>{channel?.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {error && <p className='text-danger'>Target Channel field is required for continue</p>}
              <button
                onClick={handleCopySubmit}
                type='button'
                className='btn btn-primary mt-20'
                aria-label='submit data'
              >
                Copy
              </button>
            </form>
          </Box>
        </Modal>

        <UpdateModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          data={submissionData}
          onSubmit={handleSubmit}
          isCopy={isCopy}
          level={level}
          selectedRow={selectedRow}
        />
      </>
    )
  };
}; 